import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { Button } from '@/components/Button';
import Card from '@/components/Card';
import DescriptionPopper from '@/components/DescriptionPopper';
import AlertDialog from '@/components/AlertDialog';

import { Wifi, AlertTriangle, Clock, RefreshCcw } from 'lucide-react';
import { useEquipmentContext } from '@/context/EquipmentContext';
import api from '@/services/api';
import {
  DiagnosticAbortError,
  DiagnosticTimeoutError,
  toDiagnosticsState,
  type WifiSseResult,
} from '@/services/diagnosticsSse';
import { useDiagnosticRunner } from '@/hooks/useDiagnosticRunner';
import { formatDateTime, ERROR_ICONS, parseScanResult } from '@/utils/scanner';
import { BandPanel } from './BandPanel';
import { RecommendationSection } from './RecommendationSection';
import {
  ScannerWrapper,
  TopBarContainer,
  TitleActionsWrapper,
  ButtonContentStack,
  LoadingBox,
  ErrorStateWrapper,
  ScannerToggleButtonGroup,
  ScannerToggleButton,
  BandCountChip,
  ScanActionsStack,
  ScanTimeInfoStack,
  BandHeaderGrid,
  BandSelectorStack,
  LoadingTitle,
  LoadingSubtitle,
  TopBarTitleStack,
  TopBarTitleRow,
  WarningModalHeader,
  LoadingTextStack,
} from './styles';

export interface ScannerRef {
  /** Opens the confirmation dialog that starts a new scan. */
  startTest: () => void;
}

interface ScannerProps {
  /** Reports the running state so the shared toolbar can drive the button. */
  onStateChange?: (state: { isRunning: boolean; isDone: boolean }) => void;
}

const Scanner = forwardRef<ScannerRef, ScannerProps>(function Scanner(
  { onStateChange },
  ref
) {
  const theme = useTheme();
  const { equipment, params, refetch } = useEquipmentContext();
  const { t, i18n } = useTranslation();

  const [activeBand, setActiveBand] = useState<Band>('2.4GHz');
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const { run } = useDiagnosticRunner();

  // Keep equipment refference to avoid re-rendering when equipment changes.
  const equipmentRef = useRef(equipment);
  useEffect(() => {
    equipmentRef.current = equipment;
  }, [equipment]);

  const equipmentId = equipment?.id;

  // Load initial diagnostic state or existing results when the device changes
  useEffect(() => {
    setScanResult(null);

    if (!equipmentId) return;

    let isMounted = true;
    api
      .get(`/devices/${equipmentId}/diagnostics/wifi`)
      .then((response) => {
        if (!isMounted) return;
        if (response?.data?.result) {
          const parsed = parseScanResult(response.data, equipmentRef.current);
          if (
            parsed &&
            (parsed['2.4GHz']?.networks?.length > 0 ||
              parsed['5GHz']?.networks?.length > 0)
          ) {
            setScanResult(parsed);
          }
        }
      })
      .catch(() => {
        // Silently handle initial fetch failure
      });

    return () => {
      isMounted = false;
    };
  }, [equipmentId]);

  const handleScan = useCallback(async () => {
    if (!equipment?.id) {
      enqueueSnackbar(t('scanner.errors.scanFailed'), { variant: 'error' });
      return;
    }

    setLoading(true);
    setScanResult(null);

    try {
      // fetch diagnostic parameters for Wi-Fi
      const paramsResponse = await api.get(
        `/devices/${equipment.id}/diagnostics/wifi`
      );
      const data = paramsResponse?.data;

      // extract status list from response
      interface DiagnosticStatusParam {
        id: number;
        name?: string;
        variable?: string;
        value?: string;
        writable?: number;
      }

      let statusList: DiagnosticStatusParam[] = [];
      if (Array.isArray(data?.status)) {
        statusList = data.status;
      } else if (Array.isArray(data)) {
        statusList = data.filter(
          (item: DiagnosticStatusParam) => item?.writable !== 0
        );
      } else if (data?.id) {
        statusList = [data];
      }

      // prepare payload altering necessary parameter values to 'Requested'
      const payload = statusList.map((param) => ({
        id: param.id,
        value: 'Requested',
      }));

      // Opens the SSE stream, POSTs the trigger (202) and resolves with the
      // DiagnosticResult event sent before the stream closes.
      const { result: scan } = await run<WifiSseResult>(
        String(equipment.id),
        'wifi',
        payload
      );

      // The scan reports one status per radio; collapse them so the parser
      // does not read an errored scan with no networks as a success.
      const result = parseScanResult(
        {
          ...scan,
          diagnosticsState: toDiagnosticsState(scan.status1, scan.status2),
        },
        equipment
      );
      setScanResult(result);

      const totalNetworks =
        (result['2.4GHz']?.networks?.length || 0) +
        (result['5GHz']?.networks?.length || 0);

      if (result.diagnosticsState === 'Complete') {
        enqueueSnackbar(t('scanner.networksFound', { count: totalNetworks }), {
          variant: 'success',
        });
      }
    } catch (error: unknown) {
      // A cancelled run (unmount/navigation) leaves the UI as it was.
      if (error instanceof DiagnosticAbortError) return;

      console.error('[Scanner handleScan error]', error);
      const axiosError = error as {
        response?: { data?: { diagnosticsState?: DiagnosticsState } };
        code?: string;
      };
      const diagState: DiagnosticsState =
        error instanceof DiagnosticTimeoutError
          ? 'Timeout'
          : axiosError?.response?.data?.diagnosticsState ||
            (axiosError?.code === 'ECONNABORTED' ? 'Timeout' : 'Error_Other');

      const result: ScanResult = {
        diagnosticsState: diagState,
        timestamp: new Date().toISOString(),
        '2.4GHz': {
          currentChannel: 1,
          recommendedChannel: 1,
          ranking: [],
          networks: [],
        },
        '5GHz': {
          currentChannel: 36,
          recommendedChannel: 36,
          ranking: [],
          networks: [],
        },
      };
      setScanResult(result);

      enqueueSnackbar(
        error instanceof DiagnosticTimeoutError
          ? t('tools.errors.diagnosticTimeout')
          : t('scanner.errors.scanFailed'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [equipment, run, t]);

  useImperativeHandle(ref, () => ({
    startTest: () => setScanModalOpen(true),
  }));

  // The scan is re-triggered from the toolbar, so it never reports a done
  // state: the button stays on "start scan" instead of turning into a reset.
  useEffect(() => {
    onStateChange?.({ isRunning: loading, isDone: false });
  }, [loading, onStateChange]);

  // extract current radio for activeBand from equipment context
  const activeRadio = useMemo(() => {
    if (!Array.isArray(equipment?.radios)) return undefined;
    return equipment.radios.find(
      (r: NetworkInterfaceType) =>
        r?.frequency?.value === activeBand ||
        (activeBand === '2.4GHz' && r?.frequency?.value === '2.4') ||
        (activeBand === '5GHz' && r?.frequency?.value === '5')
    );
  }, [equipment, activeBand]);

  // extract supported channel options from radio object
  const channelOptions: string[] = useMemo(() => {
    if (Array.isArray(activeRadio?.channel?.options)) {
      return activeRadio.channel.options.map(String);
    }
    return [];
  }, [activeRadio]);

  const handleOptimize = useCallback(async () => {
    if (!equipment?.id) return;
    const currentBandData = scanResult?.[activeBand];
    if (!currentBandData?.recommendedChannel) return;

    const targetChannel = currentBandData.recommendedChannel;

    // validate that targetChannel exists in the equipment radio channel options
    if (
      channelOptions.length > 0 &&
      !channelOptions.includes(String(targetChannel))
    ) {
      enqueueSnackbar(t('scanner.channelNotSupported'), {
        variant: 'warning',
      });
      return;
    }

    setOptimizing(true);

    try {
      let channelParamId: number | undefined;

      // check activeRadio directly
      if (activeRadio?.channel?.id) {
        channelParamId = Number(activeRadio.channel.id);
      }

      // check params list if not found
      if (!channelParamId && Array.isArray(params)) {
        const pattern =
          activeBand === '2.4GHz' ? /Radio\.1\.Channel/i : /Radio\.2\.Channel/i;
        const found = params.find(
          (p: ParameterType) =>
            (p?.name && pattern.test(p.name)) ||
            (p?.id && p.id === (activeBand === '2.4GHz' ? 1069 : 1087))
        );
        if (found?.id) {
          channelParamId = Number(found.id);
        }
      }

      /* Set channel id parameter for change
      2.4GHz -> 1069 Canal Device.WiFi.Radio.1.Channel
      5GHz -> 1087 Canal Device.WiFi.Radio.2.Channel*/
      if (!channelParamId) {
        channelParamId = activeBand === '2.4GHz' ? 1069 : 1087;
      }

      // PATCH to send request for change channel
      const payload = [{ id: channelParamId, value: String(targetChannel) }];
      const response = await api.patch(
        `/devices/${equipment.id}/set-parameters`,
        payload
      );

      if (response.status === 200) {
        enqueueSnackbar(
          t('scanner.optimizeSuccess', { channel: targetChannel }),
          { variant: 'success' }
        );

        // update current channel
        setScanResult((prev) => {
          if (!prev || !prev[activeBand]) return prev;
          return {
            ...prev,
            [activeBand]: {
              ...prev[activeBand],
              currentChannel: targetChannel,
            },
          };
        });

        // Trigger equipment refetch
        if (refetch) {
          refetch();
        }
      }
    } catch {
      enqueueSnackbar(t('scanner.optimizeError'), { variant: 'error' });
    } finally {
      setOptimizing(false);
    }
  }, [
    equipment,
    params,
    scanResult,
    activeBand,
    activeRadio,
    channelOptions,
    t,
    refetch,
  ]);

  const hasError =
    scanResult &&
    scanResult.diagnosticsState !== 'Complete' &&
    scanResult.diagnosticsState !== 'None';

  const currentBandData = scanResult?.[activeBand];

  // Verify if optimization is valid
  const isChannelAllowed = useMemo(() => {
    if (!currentBandData?.recommendedChannel) return false;
    if (channelOptions.length > 0) {
      return channelOptions.includes(
        String(currentBandData.recommendedChannel)
      );
    }
    return true;
  }, [channelOptions, currentBandData]);

  return (
    <ScannerWrapper>
      <TopBarContainer>
        <TitleActionsWrapper>
          <TopBarTitleStack>
            <TopBarTitleRow>
              <Typography variant="h4">{t('scanner.title')}</Typography>
              <DescriptionPopper
                description={
                  <Card maxWidth="350px">
                    <WarningModalHeader>
                      <AlertTriangle
                        color={theme.palette.secondary['01']}
                        size={36}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={theme.palette.secondary['01']}
                      >
                        {t('scanner.warningTitle')}
                      </Typography>
                    </WarningModalHeader>
                    <Typography
                      variant="caption"
                      color={theme.palette.white['06']}
                    >
                      {t('scanner.warningDescription')}
                    </Typography>
                  </Card>
                }
                placement="top"
              >
                <AlertTriangle size={24} color={theme.palette.status.warning} />
              </DescriptionPopper>
            </TopBarTitleRow>
            <Typography variant="caption" color={theme.palette.white['06']}>
              {t('scanner.subtitle')}
            </Typography>
          </TopBarTitleStack>
        </TitleActionsWrapper>

        {scanResult && (
          <ScanActionsStack>
            <ScanTimeInfoStack>
              <Clock size={14} color={theme.palette.white['06']} />
              <Typography variant="caption" color={theme.palette.white['06']}>
                {t('scanner.lastScan', {
                  date: formatDateTime(scanResult.timestamp, i18n.language),
                })}
              </Typography>
            </ScanTimeInfoStack>
          </ScanActionsStack>
        )}
      </TopBarContainer>

      <Stack gap="24px">
        {/* Loading state */}
        {loading && (
          <Card>
            <LoadingBox>
              <CircularProgress
                enableTrackSlot
                size="26px"
                sx={{ color: theme.palette.white['06'], flexShrink: 0 }}
              />
              <LoadingTextStack>
                <LoadingTitle variant="body1">
                  {t('scanner.loadingTitle')}
                </LoadingTitle>
                <LoadingSubtitle variant="caption">
                  {t('scanner.loadingDescription')}
                </LoadingSubtitle>
              </LoadingTextStack>
            </LoadingBox>
          </Card>
        )}

        {/* Error state */}
        {!loading && hasError && (
          <Card>
            <ErrorStateWrapper>
              <Box sx={{ color: theme.palette.error.main, opacity: 0.7 }}>
                {ERROR_ICONS[scanResult.diagnosticsState]}
              </Box>
              <Typography variant="h6" fontWeight={600}>
                {t(`scanner.errors.${scanResult.diagnosticsState}.title`)}
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.white['06']}
                maxWidth={420}
              >
                {t(`scanner.errors.${scanResult.diagnosticsState}.desc`)}
              </Typography>
              <Button variant="outlined" height="36px" onClick={handleScan}>
                <ButtonContentStack>
                  <RefreshCcw size={14} />
                  {t('scanner.retryButton')}
                </ButtonContentStack>
              </Button>
            </ErrorStateWrapper>
          </Card>
        )}

        {/* Empty state */}
        {!loading && !scanResult && (
          <Card>
            <ErrorStateWrapper>
              <Wifi
                size={48}
                color={theme.palette.white['06']}
                style={{ opacity: 0.4 }}
              />
              <Typography
                variant="body1"
                color={theme.palette.white['06']}
                fontWeight={500}
              >
                {t('scanner.emptyTitle')}
              </Typography>
              <Typography variant="caption" color={theme.palette.white['06']}>
                {t('scanner.emptyDescription')}
              </Typography>
            </ErrorStateWrapper>
          </Card>
        )}

        {/* Results */}
        {!loading &&
          scanResult?.diagnosticsState === 'Complete' &&
          currentBandData && (
            <Stack gap="16px" width="100%">
              {/* Band selector */}
              <BandHeaderGrid>
                <BandSelectorStack>
                  <Typography
                    variant="body2"
                    color={theme.palette.white['06']}
                    fontWeight={500}
                  >
                    {t('scanner.frequencyBand')}
                  </Typography>

                  <ScannerToggleButtonGroup
                    value={activeBand}
                    exclusive
                    onChange={(
                      _: React.MouseEvent<HTMLElement>,
                      newBand: Band | null
                    ) => {
                      if (newBand !== null) setActiveBand(newBand);
                    }}
                  >
                    {(['2.4GHz', '5GHz'] as Band[]).map((band) => (
                      <Tooltip
                        key={band}
                        title={t('scanner.networksFound', {
                          count: scanResult[band].networks.length,
                        })}
                      >
                        <ScannerToggleButton value={band}>
                          {band}
                          <BandCountChip
                            label={scanResult[band].networks.length}
                            size="small"
                            active={activeBand === band}
                          />
                        </ScannerToggleButton>
                      </Tooltip>
                    ))}
                  </ScannerToggleButtonGroup>
                </BandSelectorStack>

                {/* Recommendation Alert */}
                <RecommendationSection
                  band={currentBandData}
                  bandLabel={activeBand}
                  onOptimize={isChannelAllowed ? handleOptimize : undefined}
                  optimizing={optimizing}
                />
              </BandHeaderGrid>

              {/* Band content */}
              <BandPanel
                key={activeBand}
                band={currentBandData}
                bandLabel={activeBand}
              />
            </Stack>
          )}
      </Stack>

      <AlertDialog
        open={scanModalOpen}
        setOpen={setScanModalOpen}
        width="550px"
        title={t('scanner.modal.title')}
        content={<Typography>{t('scanner.modal.content')}</Typography>}
        confirmText={t('scanner.modal.confirm')}
        cancelText={t('scanner.modal.cancel')}
        setValue={() => handleScan()}
      />
    </ScannerWrapper>
  );
});

export default Scanner;
